import { getClient } from '../core/global-config.js';
import { createAndWaitTask } from '../core/index.js';

/**
 * 参数示例:
 * ```typescript
 * {
 *   "input": {
 *     "prompt": "一只可爱的猫咪在草地上玩耍，阳光明媚",
 *     "negative_prompt": "模糊，低质量"
 *   },
 *   "parameters": {
 *     "size": "1920*1080",
 *     "duration": 5,
 *     "seed": 12345,
 *     "prompt_extend": true,
 *     "watermark": false
 *   }
 * }
 * ```
 */
export interface AlibabaWanx22T2vPlusParams {
  /**
   * 输入参数，包含文本提示词
   */
  input: {
    /**
     * 文本提示词，描述要生成的视频内容，长度不超过800字符
     */
    prompt: string;
    /**
     * 负面提示词，描述不希望出现在视频中的内容，长度不超过500字符
     */
    negative_prompt?: string;
  };
  /**
   * 生成参数，配置视频生成选项
   */
  parameters?: {
    /**
     * 视频分辨率，支持480P档位和1080P档位。默认1920*1080
     * @default "1920*1080"
     * @values 832*480 | 480*832 | 624*624 | 1920*1080 | 1080*1920 | 1440*1440 | 1632*1248 | 1248*1632
     */
    size?: '832*480' | '480*832' | '624*624' | '1920*1080' | '1080*1920' | '1440*1440' | '1632*1248' | '1248*1632';
    /**
     * 视频时长（秒），wan2.2-t2v-plus固定为5秒，不可修改
     * @default 5
     * @values 5
     */
    duration?: 5;
    /**
     * 随机种子，用于生成可重复的结果，范围0-2147483647
     * @range 0 - 2147483647
     */
    seed?: number;
    /**
     * 是否启用提示词自动扩展，开启后会丰富和优化提示词，默认true
     * @default true
     */
    prompt_extend?: boolean;
    /**
     * 是否在生成的视频上添加水印，默认false
     * @default false
     */
    watermark?: boolean;
  };
}

export interface AlibabaWanx22T2vPlusResource {
  type: string;
  url: string;
  size?: number;
  jobId?: string;
  [key: string]: any;
}

/**
 * alibaba_wanx22_t2v_plus
 *
 * Alibaba Wanxiang 2.2 T2V Plus 视频生成接口，将文本提示词转换为高质量视频。
 * 
 * Model: wan2.2-t2v-plus
 * Features:
 * - 快速文本到视频生成
 * - 支持提示词自动扩展
 * - 支持480P和1080P两种档位的多种分辨率
 * - 支持负向提示词控制
 * - 固定5秒视频时长
 * - 可选水印功能
 *
 * @example
 * ```typescript
 * import { initSeacloud, alibabaWanx22T2vPlus } from 'seacloud-sdk';
 *
 * initSeacloud('your-api-key');
 *
 * const result = await alibabaWanx22T2vPlus(
 *   {
 *     "input": {
 *       "prompt": "一只可爱的猫咪在草地上玩耍，阳光明媚",
 *       "negative_prompt": "模糊，低质量"
 *     },
 *     "parameters": {
 *       "size": "1920*1080",
 *       "duration": 5,
 *       "seed": 12345,
 *       "prompt_extend": true,
 *       "watermark": false
 *     }
 *   }
 * );
 *
 * console.log(result); // 返回生成的资源列表
 * ```
 *
 * @param params 请求参数，详见 AlibabaWanx22T2vPlusParams 接口定义
 * @returns 生成的资源列表
 */
export async function alibabaWanx22T2vPlus(
  params: AlibabaWanx22T2vPlusParams,
): Promise<AlibabaWanx22T2vPlusResource[]> {
  const client = await getClient();

  const pollingOptions = {
    intervalMs: 5000,
    maxAttempts: 120,
  };

  const result = await createAndWaitTask(
    client,
    '/model/v1/generation',
    {
      model: 'alibaba_wanx22_t2v_plus',
      input: [{ params }],
    },
    pollingOptions,
  );

  const resources: AlibabaWanx22T2vPlusResource[] = [];

  if (result.output) {
    for (const item of result.output) {
      if (item.content) {
        for (const resource of item.content) {
          resources.push({
            type: resource.type || 'unknown',
            url: resource.url || '',
            size: resource.size,
            jobId: resource.jobId,
            ...resource,
          });
        }
      }
    }
  }

  return resources;
}
